import React from 'react'

import {
  AlignItems,
  AlignSelf,
  Body2,
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  Heading6,
  Icon,
  IconVariant,
  JustifyContent,
  Overflow,
} from '@gousto-internal/citrus-react'
import { Recipe, RecipeUUID, formatUseByDateRange } from '@library/menu-service'

import { FlexPortionSummary } from '../../../../../recipe-tile/src/components/Flex/FlexPortionSummary'
import {
  getDefaultImage,
  getMoodImage,
  getSrcSet,
  getSurchargeDisplay,
} from '../../../utils/formatters'
import { activateOnKeyboard } from './keyboard'
import { ContainedImage, PremiumBadge, RTHBadge } from './RecipeItemElements'

type FlexRecipeItemProps = {
  recipe: Recipe
  quantity: number
  numPortions: number
  removeRecipe: (recipe: Recipe) => void
  onClickRecipe: (id: RecipeUUID) => void
  onEditRecipe: (id: RecipeUUID) => void
  deliveryDate?: string
  dataNoCode?: {
    name?: string
    servings?: string
    add?: string
  }
}

export const FlexRecipeItem = ({
  recipe,
  quantity,
  numPortions,
  removeRecipe,
  onClickRecipe,
  onEditRecipe,
  deliveryDate,
  dataNoCode = {},
}: FlexRecipeItemProps) => {
  const image = getMoodImage(recipe.images)
  const surchargeDisplay = getSurchargeDisplay(recipe)
  const useByText = formatUseByDateRange(deliveryDate ?? '', recipe.shelfLife)
  const isHeatOnly = recipe.preparationType === 'heat-only'
  const handleRecipeClick = () => onClickRecipe(recipe.id)
  const handleRecipeKeyDown = (event: React.KeyboardEvent) => {
    activateOnKeyboard(event, handleRecipeClick)
  }
  const handleRemoveClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    removeRecipe(recipe)
  }
  const stopRemoveKeyPropagation = (event: React.KeyboardEvent) => {
    event.stopPropagation()
  }

  return (
    <Box
      key={recipe.id}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      bg={Color.White}
      borderRadius={1.5}
      boxShadow="0 1px 6px rgba(0, 0, 0, 0.12)"
      color={Color.ColdGrey_900}
      data-testid="recipe"
      style={{ cursor: 'pointer', borderRadius: '6px', overflow: 'hidden' }}
      onClick={handleRecipeClick}
      onKeyDown={handleRecipeKeyDown}
      role="button"
      tabIndex={0}
    >
      <Box display={Display.Flex} justifyContent={JustifyContent.FlexStart}>
        <Box
          width="110px"
          minWidth="110px"
          minHeight="108px"
          alignSelf={AlignSelf.Stretch}
          overflowX={Overflow.Hidden}
          overflowY={Overflow.Hidden}
        >
          <ContainedImage
            alt={recipe.name}
            loading="lazy"
            src={getDefaultImage(image)}
            srcSet={getSrcSet(image)}
            height="100%"
            width="110px"
          />
        </Box>
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          justifyContent={JustifyContent.FlexStart}
          flexGrow={1}
          flexShrink={1}
          minWidth="0"
          paddingH={3}
          paddingV={2}
        >
          <Box display={Display.Flex} alignItems={AlignItems.FlexStart} gap={2}>
            <Box
              display={Display.Flex}
              flexDirection={FlexDirection.Column}
              flexGrow={1}
              flexShrink={1}
              minWidth="0"
            >
              {isHeatOnly && <RTHBadge>HEAT &amp; EAT</RTHBadge>}
              <Heading6
                data-no-code={dataNoCode.name}
                style={
                  isHeatOnly
                    ? {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }
                    : {
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                      }
                }
              >
                {recipe.name}
              </Heading6>
              <Box
                display={Display.Flex}
                gap={2}
                style={{ minWidth: 0, flexWrap: 'wrap', rowGap: 0 }}
              >
                <Box color={Color.ColdGrey_600}>
                  {isHeatOnly ? (
                    <Box display={Display.Flex} alignItems={AlignItems.Center} gap={1}>
                      <Icon name="microwave" variant={IconVariant.Inherit} size={[4, 6]} />
                      <Body2 color={Color.Inherit} style={{ whiteSpace: 'nowrap' }}>
                        {recipe.prepTime} mins (x{numPortions})
                      </Body2>
                    </Box>
                  ) : (
                    <Body2 color={Color.Inherit} style={{ whiteSpace: 'nowrap' }}>
                      {recipe.prepTime} mins
                    </Body2>
                  )}
                </Box>
                <Body2 color={Color.ColdGrey_200}>|</Body2>
                <Body2 color={Color.ColdGrey_600} style={{ whiteSpace: 'nowrap' }}>
                  {recipe.nutritionalInformation.perPortion.energyKcal} kcal
                </Body2>
                {useByText && (
                  <>
                    <Body2 color={Color.ColdGrey_200}>|</Body2>
                    <Body2 color={Color.ColdGrey_600} style={{ whiteSpace: 'nowrap' }}>
                      {useByText}
                    </Body2>
                  </>
                )}
              </Box>
              {surchargeDisplay && (
                <Box
                  display={Display.Flex}
                  alignItems={AlignItems.Center}
                  gap={2}
                  style={{ paddingTop: 4 }}
                >
                  {surchargeDisplay.kind === 'premium' && <PremiumBadge>PREMIUM</PremiumBadge>}
                  <Body2 color={Color.ColdGrey_800} data-testid="recipe-surcharge">
                    {surchargeDisplay.label}
                  </Body2>
                </Box>
              )}
            </Box>
            <Box
              display={Display.Flex}
              justifyContent={JustifyContent.FlexEnd}
              minWidth="40px"
              style={{ flexShrink: 0 }}
            >
              <Button
                aria-label="Remove recipe"
                colorVariant={ButtonColorVariant.Tertiary}
                onClick={handleRemoveClick}
                onKeyDown={stopRemoveKeyPropagation}
                onKeyPress={stopRemoveKeyPropagation}
                onKeyUp={stopRemoveKeyPropagation}
                width="40px"
                style={{ height: '40px', minHeight: '40px', minWidth: '40px', padding: 0 }}
              >
                <Icon name="delete" variant={IconVariant.Actionable} size={5} />
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        display={Display.Flex}
        justifyContent={JustifyContent.FlexEnd}
        paddingH={3}
        paddingV={1}
        style={{ boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.08)' }}
      >
        <FlexPortionSummary
          recipeCount={quantity}
          portionCount={numPortions}
          onEdit={() => onEditRecipe(recipe.id)}
          stopEventPropagation
          countDataNoCode={dataNoCode.servings}
          editDataNoCode={dataNoCode.add}
        />
      </Box>
    </Box>
  )
}
